import classNames from "classnames";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useContext } from "react";
import { Handle, flexStyles, positionStyles } from "ui";
import { BlocksContext } from "../../BlocksContext";
import { InlineImage } from "../../components/InlineImage";
import { useResize } from "../../hooks/useResize";
import { Editor } from "./Editor";

// Handle width with gap in pixels
const HandleWidth = 16;

type Order = "textImage" | "imageText";

interface ImageWithChildrenBlockProps {
  image: string;
  alt: string;
  url: string;
  caption: string;
  percentageWidth: number;
  order: Order;
  blockId: string;
  updateBlock: (
    props: Partial<{
      image: string;
      alt: string;
      url: string;
      caption: string;
      percentageWidth: number;
      order: Order;
    }>,
  ) => void;
}

export function ImageWithChildrenBlock({
  image,
  alt,
  url,
  caption,
  percentageWidth,
  order,
  blockId: _blockId,
  updateBlock,
}: ImageWithChildrenBlockProps) {
  const { NewImage, EditPopover } = useContext(BlocksContext);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [containerWidth, setContainerWidth] = useState<number | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const { onMouseDown, onTouchStart, width } = useResize(imageContainerRef, { maxWidth: null });

  const updateContainerWidth = useCallback(() => {
    const parentElement = containerRef.current?.parentElement?.parentElement?.parentElement;

    if (!parentElement) {
      setTimeout(updateContainerWidth, 0);
      return;
    }

    const width = parentElement?.clientWidth;
    setContainerWidth(width);
  }, []);

  useLayoutEffect(() => {
    updateContainerWidth();
  }, [updateContainerWidth]);

  useEffect(() => {
    updateContainerWidth();

    const parentContainer = containerRef.current?.parentElement?.parentElement?.parentElement;
    if (!parentContainer) return;

    const resizeObserver = new ResizeObserver(() => {
      updateContainerWidth();
    });

    resizeObserver.observe(parentContainer);

    return () => {
      resizeObserver.disconnect();
    };
  }, [updateContainerWidth]);

  // Calculate percentageWidth based on width
  useEffect(() => {
    if (containerWidth && imageContainerRef.current && width !== Number.POSITIVE_INFINITY) {
      const newPercentage = Math.round(((width + HandleWidth / 2) / containerWidth) * 100);

      if (newPercentage !== percentageWidth) {
        updateBlock({ percentageWidth: newPercentage });
      }
    }
  }, [width, percentageWidth, updateBlock, containerWidth]);

  const handleUpdate = (
    values: Partial<{
      image: string;
      alt: string;
      url: string;
      caption: string;
    }>,
  ) => {
    updateBlock(values);
  };

  // Render the Image component
  const ImageComponent = (
    <div className={classNames(flexStyles.vert, flexStyles.alignCenter)}>
      {image ? (
        <InlineImage image={image} alt={alt} url={url} caption={caption} />
      ) : (
        <NewImage onUpload={(imageUrl: string) => updateBlock({ image: imageUrl })} />
      )}
      <EditPopover
        isOpen={isEditorOpen}
        onOpen={() => setIsEditorOpen(true)}
        showEditButton={!!image}
        editor={
          <Editor
            alt={alt}
            url={url}
            caption={caption}
            onUpdate={handleUpdate}
            onClose={() => setIsEditorOpen(false)}
          />
        }
      />
    </div>
  );

  return (
    <div
      data-block-order={order}
      ref={containerRef}
      className={classNames(positionStyles.relative, flexStyles.horiz025)}
    >
      {order === "textImage" && (
        <Handle
          className={flexStyles.alignSelfCenter}
          onMouseDown={(event) => onMouseDown(event, "negative")}
          onTouchStart={(event) => onTouchStart(event, "negative")}
        />
      )}
      <div
        style={containerWidth ? { width: `${(percentageWidth * containerWidth) / 100}px` } : {}}
        ref={imageContainerRef}
        className={classNames(positionStyles.relative)}
      >
        {ImageComponent}
      </div>
      {order === "imageText" && (
        <Handle
          className={flexStyles.alignSelfCenter}
          onMouseDown={(event) => onMouseDown(event, "positive")}
          onTouchStart={(event) => onTouchStart(event, "positive")}
        />
      )}
    </div>
  );
}
